import { Link, Navigate, useParams } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { t as getFixedT } from "@/i18n";
import {
  isSupportedLanguage,
  localeUrl,
  type SupportedLanguage,
} from "@/lib/site";
import "./PrivacyPage.css";

/**
 * `/ar/privacy` + `/en/privacy` — linked from the lead form's required consent checkbox
 * (09-landing-page.md §3 "label links to `/privacy`, opens in new tab") and the footer legal link.
 * Placeholder policy text pending real legal copy sign-off (same "assumption, not a structural
 * gap" pattern as the rest of the brand placeholders in `src/lib/brand.ts`) — but a real, working,
 * indexable page rather than a dead `href="#"`, since the consent checkbox's linked text must be
 * a genuine, keyboard-reachable link per §3.3 accessibility notes.
 */
export function PrivacyPage() {
  const { lang } = useParams<{ lang: string }>();

  if (!isSupportedLanguage(lang)) {
    return <Navigate to="/ar/privacy" replace />;
  }

  return <LocalePrivacy lang={lang} />;
}

function LocalePrivacy({ lang }: { lang: SupportedLanguage }) {
  const t = getFixedT(lang);
  const sections = t("privacy.sections", { returnObjects: true }) as {
    heading: string;
    body: string;
  }[];

  return (
    <>
      <Seo
        lang={lang}
        title={t("privacy.metaTitle")}
        description={t("privacy.metaDescription")}
        canonical={`${localeUrl(lang)}/privacy`}
      />
      <main id="main-content" className="fa-privacy">
        <div className="fa-privacy__inner">
          <Link to={`/${lang}`} className="fa-privacy__back">
            {t("privacy.backHome")}
          </Link>
          <h1>{t("privacy.heading")}</h1>
          <p className="fa-privacy__updated">{t("privacy.lastUpdated")}</p>
          {sections.map((section) => (
            <section key={section.heading}>
              <h2>{section.heading}</h2>
              <p>{section.body}</p>
            </section>
          ))}
        </div>
      </main>
    </>
  );
}
